import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, Renderer2, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NCatalogUi } from '@df/catalog-ui/df-catalog-ui.config';
import { IListingSetup } from '@df/catalog-ui/listing/listing';
import { ProductListing } from '@df/catalog-ui/listing/product-listing';
import { ProductListingFactory } from '@df/catalog-ui/listing/product-listing.factory';
import { IListingSizeOption } from '@df/catalog-ui/listing/size/listing-size';
import { IListingSortOption } from '@df/catalog-ui/listing/sort/listing-sort';
import { Attribute } from '@df/catalog/attribute/attribute';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { ICategoryDataV3 } from '@df/catalog/category/category';
import { LocalCategory } from '../../../category/category.decorator';
import { Product as CoreProduct, IProductDataV2, IProductDataV3 } from '@df/catalog/product/product';
import { ProductConfiguration } from '@df/module-product-configuration/model/product-configuration';
import { BodyClassService } from '@df/ui/body-class.service';
import { PhotoswipeUiDefaultOptions } from '@df/ui/photoswipe/photoswipe.types';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { BehaviorSubject } from 'rxjs';
import { Product } from '../../product.decorator';
import { ProductViewComponent } from '../product-view.component';
import { GoogleShoppingCategoryService } from './google-shopping-category.service';

export namespace NProductViewLayout {
    export const BLOCK_ID = 'cms-product-view-layout';

    export type IBlockData = Catalog.IBlockData<IBlockDataContent>;

    export interface IBlockDataContent {
        specification?: string[];
        /**
         * Bottom components list
         */
        bottomComponents: NICmsContentComponent.IData[];
    }

    export interface ISpecification {
        attrCode: string;
    }

    export enum ESpecSectionType {
        simple = 'simple',
        outlets = 'outlets'
    }

    export const specAttrCodes = [
        'supplier_name',
        'filter_brand',
        'config_att_length',
        'filter_thickness',
        'filter_depth',
        'filter_width',
        'filter_colour',
        'filter_diameter'
    ];
}

/**
 * Component definition
 */
@Component({
    selector: 'cms-product-view-layout',
    templateUrl: './product-view-layout.html',
    styleUrls: ['./product-view-layout.scss']
})
@ICmsComponent<ProductViewLayoutComponent>({
    id: NProductViewLayout.BLOCK_ID,
    block: {
        url: NProductViewLayout.BLOCK_ID
    },
    onDemandOnly: true,
    name: 'Product page layout',
    description: 'Product page layout block',
    fields: [
        <NICmsContentField.IRelationField>{
            key: 'specification',
            type: NICmsContentField.EFieldType.relation,
            title: 'Specification attributes',
            options: {
                title: 'Attribute',
                type: 'attribute',
                multiple: true,
                filter: (model: Attribute) => !!model.data?.code && !!model.data?.label
            },
            default: NProductViewLayout.specAttrCodes
        },
        <NICmsContentField.IComponentsField>{
            key: 'bottomComponents',
            type: NICmsContentField.EFieldType.components,
            title: 'Bottom components',
            itemName: 'component',
            default: []
        }
    ]
})
export class ProductViewLayoutComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit, OnChanges, OnDestroy {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NProductViewLayout.IBlockDataContent;
    private elementRef = inject(ElementRef);
    private renderer2 = inject(Renderer2);
    private bodyClassService = inject(BodyClassService);
    private activatedRoute = inject(ActivatedRoute);
    private router = inject(Router);
    private productListingFactory = inject(ProductListingFactory);
    private catalogUiConfig = inject(NCatalogUi.OPTIONS);
    private googleShoppingCategoryService = inject(GoogleShoppingCategoryService);
    public photoswipeIndex = new BehaviorSubject(0);
    public photoswipeOptions: Partial<PhotoswipeUiDefaultOptions> = {};
    public vatListingCategory?: LocalCategory<ICategoryDataV3>;
    public vatListing?: ProductListing<CoreProduct<IProductDataV2>>;
    public isGoogleShoppingLayout = false;
    public isGoogleShoppingCategoryLoading = false;

    @Input()
    variant?: string;

    protected override canDetach = false;

    /**
     * Product shorthand
     */
    @Input({
        required: true
    })
    product!: Product<IProductDataV3>;

    /**
     * Parent product shorthand
     */
    @Input({
        required: true
    })
    parent: Product<IProductDataV3> | undefined;

    // even if not used it is needed to initialize before component renders
    private productConfiguration = inject(ProductConfiguration);

    /**
     * Product view component
     */
    readonly productView = inject(ProductViewComponent);

    override ngOnInit(): void {
        const element = this.elementRef.nativeElement;

        this.photoswipeOptions = {
            showAnimationDuration: 250,
            hideAnimationDuration: 250,
            pinchToClose: false,
            closeOnScroll: false,
            closeOnVerticalDrag: false,
            tapToToggleControls: false,
            hiddenControlsMobile: false,
            counterEl: false,
            barsSize: { top: 0, bottom: 'auto' },
            initialZoom: 1,
            followMouse: true,
            canPreload: false,
            timeToIdle: 0,
            getDoubleTapZoom: (isMouseClick, item: any) => {
                if (!item.zoomLevel) {
                    item.zoomLevel = item.initialZoomLevel;
                }

                let res: number;

                if (item.zoomLevel < 0.5 && (this.client.isM || this.client.isS)) {
                    res = 0.5;

                    this.renderer2.addClass(element, 'photoswipe--zoom-50');
                    this.renderer2.removeClass(element, 'photoswipe--zoom-100');
                } else if (item.zoomLevel < 1) {
                    res = 1;

                    this.renderer2.addClass(element, 'photoswipe--zoom-100');
                    this.renderer2.removeClass(element, 'photoswipe--zoom-50');
                } else {
                    res = item.initialZoomLevel;

                    this.renderer2.removeClass(element, 'photoswipe--zoom-50');
                    this.renderer2.removeClass(element, 'photoswipe--zoom-100');
                }

                item.zoomLevel = res;

                return res;
            }
        };

        this.initGoogleShoppingLayout();
        super.ngOnInit();
    }

    get specificationsAttr() {
        return this.content?.specification || [];
    }

    private async initGoogleShoppingLayout(): Promise<void> {
        this.isGoogleShoppingLayout = this.activatedRoute.snapshot.queryParamMap.get('vat') === '1';

        if (!this.isGoogleShoppingLayout) {
            return;
        }

        this.isGoogleShoppingCategoryLoading = true;
        const category = await this.googleShoppingCategoryService.loadCategory(this.product, this.parent);
        if (category?.data) {
            this.vatListingCategory = category;
            this.vatListing = this.createVatListing(category);
        }
        this.isGoogleShoppingCategoryLoading = false;
        this.detectChanges();
    }

    exitGoogleShoppingLayout(): void {
        this.vatListing?.clearSubscriptions();
        this.vatListing = undefined;
        this.vatListingCategory = undefined;
        this.isGoogleShoppingLayout = false;
        this.isGoogleShoppingCategoryLoading = false;
        void this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: { vat: null },
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
        this.detectChanges();
    }

    private createVatListing(category: LocalCategory<ICategoryDataV3>): ProductListing<CoreProduct<IProductDataV2>> {
        const categoryConfig = this.catalogUiConfig.listing.category;
        const listingConfig = {
            type: 'product',
            source: category,
            infScrollStep: categoryConfig.infScrollStep,
            infScrollInit: categoryConfig.infScrollInit,
            pushOOStoBottom: category.data?.push_oos_to_bottom,
            storage: false,
            custom: {
                pioAllowed: true,
                isMain: false
            }
        };

        const listingSetup: IListingSetup<CoreProduct<IProductDataV2>> = {
            filtersDefinitions: category.data?.filters || [],
            sortOptions: categoryConfig.sort as IListingSortOption[],
            sizeOptions: categoryConfig.size as IListingSizeOption[],
            defaultSize: categoryConfig.defaultSize
        };

        return this.productListingFactory.create<CoreProduct<IProductDataV2>>(listingConfig, category.data?.products || [], listingSetup);
    }

    /////////////////// HELPERS ////////////////////

    override ngOnDestroy(): void {
        this.vatListing?.clearSubscriptions();
        super.ngOnDestroy();
    }

    override detectChanges() {
        this.client.isCms ? this.productView.detectChanges() : super.detectChanges();
    }

    ////////////////// Photoswiper //////////////////

    onPhotoswipeStateChange(state: boolean) {
        this.bodyClassService.set('photoswipe-open', this, state);

        state
            ? this.renderer2.addClass(this.elementRef.nativeElement, 'photoswipe--active')
            : this.renderer2.removeClass(this.elementRef.nativeElement, 'photoswipe--active');
        this.renderer2.addClass(this.elementRef.nativeElement, 'photoswipe--inactive');

        this.timeoutService.setTimeout(() => {
            this.renderer2.removeClass(this.elementRef.nativeElement, 'photoswipe--inactive');
        }, 500);
    }

    onPhotoswipeChanged(index: number) {
        this.photoswipeIndex.next(index);
    }
}
